import {
    useFirstCompanyPeriod,
    useLastCompanyPeriod,
    useFirstPeriodForProcessedInvoices,
    useLatestPeriodForProcessedInvoices,
} from '../../redux/selectors'

import { periodFromDate } from '../../utils/DateUtils'
import { PeriodRange, UNBOUNDED_PERIODS } from './PeriodBounds'

/**
 * Bound sources a period picker can be restricted to. Bounds are opt-in: a
 * picker declaring no source can reach any period, which is what date columns
 * (contract end dates, terminations, audit timestamps) need.
 */
export const PERIOD_BOUNDS = {
    /** The company's published periods. For columns that hold one of those periods. */
    COMPANY: 'companyPeriods',
    /** The periods that have processed invoices. */
    INVOICES: 'invoicePeriods',
    /** Company start through the end of next year, for periods that are planned ahead. */
    PLANNING: 'planningPeriods',
}

const endOfNextYear = () => ({ year: periodFromDate(new Date()).year + 1, month: 12 })

/** Table dispatch: one entry per source, so there is no switch to extend. */
const BOUNDS_BUILDERS = {
    // Bounded by the plain `lastCompanyPeriod`, not by `useLastStartedCompanyPeriod`:
    // a period the company opened ahead of this month is a legitimate thing to select
    // here, it is only a poor default to *open* on. Pages handle that themselves.
    [PERIOD_BOUNDS.COMPANY]: periods => new PeriodRange(periods.firstCompanyPeriod, periods.lastCompanyPeriod),
    [PERIOD_BOUNDS.INVOICES]: periods => new PeriodRange(periods.firstInvoicePeriod, periods.lastInvoicePeriod),
    [PERIOD_BOUNDS.PLANNING]: periods => new PeriodRange(periods.firstCompanyPeriod, endOfNextYear()),
}

const buildUnbounded = () => UNBOUNDED_PERIODS

/**
 * Resolves a bound source into a PeriodBounds strategy. Every selector is read
 * unconditionally so hook order stays stable regardless of the source.
 */
export default function usePeriodBounds(source) {
    const periods = {
        firstCompanyPeriod: useFirstCompanyPeriod(),
        lastCompanyPeriod: useLastCompanyPeriod(),
        firstInvoicePeriod: useFirstPeriodForProcessedInvoices(),
        lastInvoicePeriod: useLatestPeriodForProcessedInvoices(),
    }

    return (BOUNDS_BUILDERS[source] || buildUnbounded)(periods)
}
